// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/useWordClamp.test.tsx

/**
 * Clamp a text to N lines and end it at a whole word.
 *
 * `-webkit-line-clamp` ends the last line where the line ends, which is mid-word
 * ("anchor i...", "Magnesiu..." at the rail's widths). Nothing in CSS cuts at a
 * word, so the cut is measured: a hidden probe with the card text's own
 * typography is filled with the longest run of whole words that still fits in N
 * lines, and the visible text shows that run.
 */

import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/**
 * The longest start of `text`, in whole words and closed with an ellipsis, that
 * `fits`; null when all of the text fits.
 *
 * Always keeps one word: a word wider than the box is cut by CSS, nothing here
 * can do better. `fits` must be monotonic (more words never fit where fewer did
 * not), which a height measure is.
 */
export function fitWordPrefix(
  text: string,
  fits: (candidate: string) => boolean,
): string | null {
  if (fits(text)) return null;
  const words = text.split(/\s+/).filter(Boolean);
  const join = (count: number) => `${words.slice(0, count).join(" ")}…`;
  let low = 1;
  let high = words.length - 1;
  let best = 1;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (fits(join(mid))) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  const body = words
    .slice(0, best)
    .join(" ")
    .replace(/[\s,;:.—-]+$/, "");
  return `${body}…`;
}

export interface WordClamp {
  /** On the hidden probe element, a sibling of the visible text inside one box. */
  probeRef: RefObject<HTMLSpanElement>;
  /** The text to show instead of the whole, or null to show it as it is. */
  clipped: string | null;
}

/**
 * @param lineHeightPx the text's line height in pixels, the unit a line count is measured in.
 */
export function useWordClamp(
  text: string,
  lines: number,
  lineHeightPx: number,
  enabled: boolean,
): WordClamp {
  const probeRef = useRef<HTMLSpanElement>(null);
  const [clipped, setClipped] = useState<string | null>(null);

  const measure = useCallback(() => {
    const probe = probeRef.current;
    if (!probe || !enabled) {
      setClipped(null);
      return;
    }
    // A pixel of slack for sub-pixel line boxes.
    const limit = lineHeightPx * lines + 1;
    const fits = (candidate: string) => {
      probe.textContent = candidate;
      return probe.scrollHeight <= limit;
    };
    const result = fitWordPrefix(text, fits);
    probe.textContent = "";
    setClipped(result);
  }, [text, lines, lineHeightPx, enabled]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useLayoutEffect(() => {
    const box = probeRef.current?.parentElement;
    if (!box) return undefined;
    let width = box.getBoundingClientRect().width;
    const onResize = () => {
      const next = box.getBoundingClientRect().width;
      if (next === width) return;
      width = next;
      measure();
    };
    // why guarded: jsdom has no ResizeObserver, and no layout to measure either; there the text keeps its CSS clamp.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(onResize);
    observer?.observe(box);
    // why again after fonts load: the app font changes every line's width.
    let live = true;
    const fonts = typeof document !== "undefined" ? document.fonts : undefined;
    void fonts?.ready.then(() => {
      if (live) measure();
    });
    return () => {
      live = false;
      observer?.disconnect();
    };
  }, [measure]);

  return { probeRef, clipped };
}
